import { serializeDatabase } from '@databases/ai-first-creation-serializer';
import { YDatabase } from '@databases/collab/y-database';
import { collabStateManager } from '@databases/collab-state-manager';
import {
  getBaseUrl,
  getContentIdFromPath,
  getContentIdFromQueryParams,
  getParentWindow,
} from '@databases/database-state';
import { appStore } from '@databases/redux/store';
import { type ObjectMode } from '@databases/types';
import { type Database } from '@databases/types/database';
import { createCollabDatabase } from '@databases/utils/database/service';
import { addDatabaseToList } from '@databases/utils/redux/databases/actions';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getDatabasePath } from '@databases/utils/urls';
import { v4 as generateId } from 'uuid';

import { type ClientId } from '@canvas/rovo-content-bridge-api';

import { bridgeEventEmitter } from './bridge-command-emitter';
import { DatabaseStreamState } from './database-stream-state';
import { createDatabase } from './handlers/create-database-handler';
import { editDatabase } from './handlers/edit-database-handler';
import { readDatabaseContent } from './handlers/read-database-content-handler';
import { type RovoContentBridgeAPIShape } from './rovo-bridge-types';

const mockBridge = {
  send: () => {},
} as unknown as RovoContentBridgeAPIShape;

bridgeEventEmitter.initBridge(mockBridge);

const DEFAULT_TEST_PARAMS = {
  key: 'test-key',
  senderId: 'test-sender-id' as ClientId,
  receiverId: 'test-receiver-id' as ClientId,
  singleInstrumentationID: undefined,
};

async function* chunkString(input: string, chunkSize = 100) {
  for (let i = 0; i < input.length; i += chunkSize) {
    yield input.slice(i, i + chunkSize);
  }
}

const processChunks = async (
  chunks: AsyncGenerator<string, void, unknown> | Generator<string, void, unknown>,
  contentId: string,
  groupId: string,
  operationType: 'create' | 'edit',
  title: string,
  delay = 0
) => {
  if (operationType === 'edit') {
    await readDatabaseContent(contentId);
  }

  try {
    for await (const chunk of chunks) {
      if (operationType === 'create') {
        await createDatabase(
          {
            isStreamComplete: false,
            data: {
              title,
              document: chunk,
            },
            ...DEFAULT_TEST_PARAMS,
            groupId,
            singleInstrumentationID: 'test-single-instrumentation-id',
          },
          contentId
        );
      } else {
        await editDatabase(
          {
            isStreamComplete: false,
            data: {
              document: chunk,
              title: 'test-database',
            },
            ...DEFAULT_TEST_PARAMS,
            groupId,
            singleInstrumentationID: 'test-single-instrumentation-id',
          },
          contentId
        );
      }

      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    if (operationType === 'create') {
      await createDatabase(
        {
          isStreamComplete: true,
          data: {
            document: '',
            title: 'test-database',
          },
          ...DEFAULT_TEST_PARAMS,
          groupId,
          singleInstrumentationID: 'test-single-instrumentation-id',
        },
        contentId
      );
    } else {
      await editDatabase(
        {
          isStreamComplete: true,
          data: {
            document: '',
            title: 'test-database',
          },
          ...DEFAULT_TEST_PARAMS,
          groupId,
          singleInstrumentationID: 'test-single-instrumentation-id',
        },
        contentId
      );
    }
  } catch (error) {
    console.error('Error processing chunks', error);
    throw error;
  } finally {
    await DatabaseStreamState.getInstance().completeStreaming();
  }
};

const importFromString = async (
  csvString: string,
  operationType: 'create' | 'edit',
  delay = 0
) => {
  const sessions = collabStateManager.listSessions();
  const contentId = sessions[0];
  const groupId = `${contentId}-${Math.random().toString(36).substring(2, 15)}`;
  const chunks = chunkString(csvString);

  await processChunks(chunks, contentId, groupId, operationType, 'test-database', delay);
};

const transformToContext = async (json: string) => {
  const jsonObject = JSON.parse(json);
  const database = YDatabase.fromJSON(jsonObject, {
    getCurrentUser: () => {
      throw new Error();
    },
  });

  const result = serializeDatabase(database, new Set(), {});

  return result.output;
};

const importFromJSON = async (json: string, contentId: string) => {
  const jsonObject = JSON.parse(json);
  const database = YDatabase.fromJSON(jsonObject, {
    getCurrentUser: () => {
      throw new Error();
    },
  });

  collabStateManager.getCollabManager(contentId)?.database.replace(database.toJSON());
};

const importFromGenerator = async (
  contentId: string,
  chunkGenerator: AsyncGenerator<string, void, unknown>,
  operationType: 'create' | 'edit',
  title = 'test-database'
) => {
  const groupId = `${contentId}-${Math.random().toString(36).substring(2, 15)}`;
  await processChunks(chunkGenerator, contentId, groupId, operationType, title, 0);
};

async function readContent(contentId: string): Promise<string> {
  const content = await readDatabaseContent(contentId);

  if ('csv' in content && typeof content.csv === 'string') {
    return content.csv;
  }

  return '';
}

interface DuplicateDatabaseParams {
  name?: string;
  spaceKey?: string;
}

const duplicateDatabase = async (
  params: DuplicateDatabaseParams = {}
): Promise<string> => {
  const contentId =
    getContentIdFromQueryParams() ??
    getContentIdFromPath(getParentWindow().location.pathname);

  if (!contentId) {
    throw new Error('No active database session found');
  }

  const state = appStore.getState();
  const sourceDatabase = selectDatabaseByContentIdRequired(state, {
    contentId,
  });

  const name = params.name ?? `Copy of ${sourceDatabase.name}`;
  const spaceKey = params.spaceKey ?? sourceDatabase.space.key;

  const databaseId = generateId();
  const databaseDraft: Database<ObjectMode.CREATE> = {
    id: databaseId,
    contentId: databaseId,
    type: 'database',
    name,
    space: { key: spaceKey },
    fields: sourceDatabase.fields,
    fieldsById: sourceDatabase.fieldsById,
    defaultEntry: sourceDatabase.defaultEntry,
    entries: sourceDatabase.entries,
    entriesById: sourceDatabase.entriesById,
    meta: {
      id: databaseId,
      isLocked: false,
    },
    coverImage: undefined,
    views: sourceDatabase.views,
    defaultViewId: sourceDatabase.defaultViewId,
    commentThreads: {},
  };

  const database = await createCollabDatabase(databaseDraft, appStore);

  appStore.dispatch(addDatabaseToList(database));

  const baseUrl = getBaseUrl();
  const path = getDatabasePath({
    spaceKey: database.space.key,
    contentId: database.contentId,
  });

  return baseUrl + path;
};

export { importFromString, importFromGenerator, duplicateDatabase };

declare global {
  interface Window {
    aiFirstCreation: {
      importFromString: (
        csvString: string,
        operationType: 'create' | 'edit',
        delay?: number
      ) => Promise<void>;
      transformToContext: (json: string) => Promise<string>;
      importFromJSON: (json: string, contentId: string) => Promise<void>;
      readContent: (contentId: string) => Promise<string>;
      getErrors: () => unknown[];
      clearErrors: () => void;
      duplicateDatabase: (params?: {
        name?: string;
        spaceKey?: string;
      }) => Promise<string>;
      isAllDataHandled: () => boolean;
    };
  }
}

(globalThis as any).aiFirstCreation = {
  importFromString,
  readContent,
  getErrors: () => {
    return DatabaseStreamState.getInstance().getErrors();
  },
  clearErrors: () => {
    DatabaseStreamState.getInstance().clearErrors();
  },
  transformToContext,
  importFromJSON,
  duplicateDatabase,
  isAllDataHandled: () => DatabaseStreamState.getInstance().isAllDataHandled,
};
